import React, {
  useEffect, useRef, forwardRef, useImperativeHandle, useState,
} from "react";
import * as Sentry from "@sentry/react";

const READY_POLL_INTERVAL_MS = 50;
const READY_TIMEOUT_MS = 10000;

const TurnstileWrapper = forwardRef(
  ({
    onChange, captchaKey, setTurnstileInteractive, onError, action, emitNullOnReset,
  }, ref) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onErrorRef = useRef(onError);

    const [scriptLoaded, setScriptLoaded] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(null);

    useEffect(() => {
      onErrorRef.current = onError;
    }, [onError]);

    // expose reset/remove to parent
    useImperativeHandle(ref, () => ({
      reset: () => {
        try {
          if (window.turnstile && widgetIdRef.current != null) {
            window.turnstile.reset(widgetIdRef.current);
            setTurnstileToken(null);
            if (emitNullOnReset) {
              onChange?.(null);
            }
            setTurnstileInteractive(true);
          }
        } catch (err) {
          console.warn("Turnstile reset failed:", err);
        }
      },
      remove: () => {
        try {
          if (window.turnstile && widgetIdRef.current != null) {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
          }
        } catch (err) {
          console.warn("Turnstile remove failed:", err);
        }
      },
    }));

    // wait for the globally-loaded turnstile object — script tag lives in HTML, not here
    useEffect(() => {
      if (window.turnstile) {
        setScriptLoaded(true);
        return;
      }

      let elapsed = 0;
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          setScriptLoaded(true);
          return;
        }
        elapsed += READY_POLL_INTERVAL_MS;
        if (elapsed >= READY_TIMEOUT_MS) {
          clearInterval(interval);
          onErrorRef.current?.(new Error("Turnstile script did not become ready in time"));
        }
      }, READY_POLL_INTERVAL_MS);

      return () => clearInterval(interval);
    }, []);

    // render widget once script + container are ready
    useEffect(() => {
      if (!scriptLoaded) {
        return;
      }
      if (!containerRef.current) {
        return;
      }
      if (!window.turnstile) {
        return;
      }
      if (widgetIdRef.current != null) {
        return;
      } // already rendered

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: captchaKey,
          action,
          appearance: "interaction-only",
          callback: (token) => {
            setTurnstileToken(token);
            onChange?.(token);
          },
          "expired-callback": () => {
            setTurnstileToken(null);
            onChange?.(null);
            setTurnstileInteractive(true);
          },
          "error-callback": (err) => onError?.(err),
          "before-interactive-callback": () => setTurnstileInteractive(true),
          "after-interactive-callback": () => setTurnstileInteractive(false),
          "unsupported-callback": (e = {}) => {
            Sentry.captureException(new Error("Browser does not support Turnstile"), {
              contexts: { data: { action, args: e } },
            });
          },
        });
      } catch (e) {
        onError?.(e);
      }
    }, [scriptLoaded, captchaKey, action, onChange, onError, setTurnstileInteractive]);

    return (
      <>
        <div ref={containerRef} />
        <input type="hidden" name="cf-turnstile-response" value={turnstileToken || ""} readOnly />
      </>
    );
  },
);

export default TurnstileWrapper;
